import { useState } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";

function EmployeeLogin() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (event) => {
    event.preventDefault();

    setMessage("");
    setLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();

      const loginResult = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );

      const employeeQuery = query(
        collection(db, "employees"),
        where("email", "==", normalizedEmail)
      );

      const employeeSnapshot = await getDocs(employeeQuery);

      if (employeeSnapshot.empty) {
        await signOut(auth);
        setMessage("This account is not registered as an employee.");
        return;
      }

      const employeeDocument = employeeSnapshot.docs[0];
      const employeeData = employeeDocument.data();

      if (employeeData.status !== "Active") {
        await signOut(auth);
        setMessage(
          "Your employee account is inactive. Please contact the admin."
        );
        return;
      }

      localStorage.setItem(
        "crmEmployee",
        JSON.stringify({
          id: employeeDocument.id,
          uid: loginResult.user.uid,
          name: employeeData.name || "Employee",
          email: employeeData.email || normalizedEmail,
        })
      );

      navigate("/employee-dashboard");
    } catch (error) {
      console.error("Employee login error:", error);

      if (
        error.code === "auth/invalid-credential" ||
        error.code === "auth/wrong-password" ||
        error.code === "auth/user-not-found"
      ) {
        setMessage("Incorrect email address or password.");
      } else if (error.code === "auth/too-many-requests") {
        setMessage(
          "Too many login attempts. Please try again later."
        );
      } else if (error.code === "auth/network-request-failed") {
        setMessage(
          "Internet connection problem. Please try again."
        );
      } else {
        setMessage("Unable to log in. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-card">
        <div className="admin-login-brand">
          <div className="brand-mark">TW</div>

          <div>
            <h1>Trading Wealth CRM</h1>
            <p>Employee Portal</p>
          </div>
        </div>

        <div className="admin-login-heading">
          <h2>Employee Login</h2>

          <p>
            Sign in to view assigned customers, update remarks and
            manage callbacks.
          </p>
        </div>

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="employee-login-email">
              Email address
            </label>

            <input
              id="employee-login-email"
              type="email"
              value={email}
              onChange={(event) =>
                setEmail(event.target.value)
              }
              placeholder="Enter your employee email"
              autoComplete="email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="employee-login-password">
              Password
            </label>

            <div className="password-field">
              <input
                id="employee-login-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) =>
                  setPassword(event.target.value)
                }
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />

              <button
                type="button"
                className="show-password-button"
                onClick={() =>
                  setShowPassword(
                    (currentValue) => !currentValue
                  )
                }
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {message && (
            <div className="login-message">
              {message}
            </div>
          )}

          <button
            type="submit"
            className="admin-login-button"
            disabled={loading}
          >
            {loading
              ? "Signing in..."
              : "Sign in as Employee"}
          </button>
        </form>

        <button
          type="button"
          className="employee-admin-login-link"
          onClick={() => navigate("/")}
        >
          Go to Admin Login
        </button>

        <p className="admin-login-footer">
          Authorized Trading Wealth employees only
        </p>
      </div>
    </div>
  );
}

export default EmployeeLogin;